import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'link' | 'secondary' | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm' | 'icon-md' | 'icon-lg';
  asChild?: boolean;
}

const variantClasses: Record<string, string> = {
  primary: [
    'bg-brand-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset',
    'hover:bg-brand-solid_hover',
    'disabled:bg-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  ].join(' '),
  secondary: [
    'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset',
    'hover:bg-primary_hover hover:text-secondary_hover',
    'disabled:bg-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  ].join(' '),
  outline: [
    'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset',
    'hover:bg-primary_hover hover:text-secondary_hover',
    'disabled:bg-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  ].join(' '),
  ghost: [
    'text-tertiary',
    'hover:bg-primary_hover hover:text-tertiary_hover',
    'disabled:text-fg-disabled',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  ].join(' '),
  link: [
    'text-brand-secondary !p-0',
    'hover:text-brand-secondary_hover hover:underline',
    'disabled:text-fg-disabled',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  ].join(' '),
  destructive: [
    'bg-error-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset',
    'hover:bg-error-solid_hover',
    'disabled:bg-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error',
  ].join(' '),
};

const sizeClasses: Record<string, string> = {
  sm: 'h-8 gap-1 rounded-lg px-3 py-2 text-sm font-semibold',
  md: 'h-10 gap-1 rounded-lg px-3.5 py-2.5 text-sm font-semibold',
  lg: 'h-12 gap-1.5 rounded-lg px-4 py-2.5 text-md font-semibold',
  icon: 'size-8 rounded-lg p-0',
  'icon-sm': 'size-6 rounded-lg p-0',
  'icon-md': 'size-8 rounded-lg p-0',
  'icon-lg': 'size-10 rounded-lg p-0',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(
          'relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap transition duration-100 ease-linear',
          'disabled:cursor-not-allowed',
          '*:data-icon:pointer-events-none *:data-icon:size-5 *:data-icon:shrink-0',
          sizeClasses[size],
          variantClasses[variant],
          className,
        )}
        disabled={disabled}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
